import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { RefreshCw, Download, AlertTriangle, Eye, EyeOff, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const UncategorizedProductsPanel = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [hideHidden, setHideHidden] = useState(false);
  // Per-product overrides (category id chosen by admin) + selection set
  const [overrides, setOverrides] = useState({});      // { product_id: category_id }
  const [selected, setSelected] = useState(new Set()); // product_ids checked
  // Apply flow state
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResults, setApplyResults] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [unc, cats] = await Promise.all([
        fetch(`${API_BASE}/admin/products/uncategorized`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API_BASE}/admin/ecommerce-categories`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setData(unc);
      setCategories(cats?.categories || []);
      // Initialize overrides with the server-side suggestions
      const init = {};
      (unc?.products || []).forEach(p => {
        if (p.suggested_category_id) init[p.id] = p.suggested_category_id;
      });
      setOverrides(init);
      setSelected(new Set());
      setApplyResults(null);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => (data?.products || []).filter((p) => {
    if (hideHidden && p.hidden) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (p.ref || '').toLowerCase().includes(s) || (p.name || '').toLowerCase().includes(s);
  }), [data, hideHidden, search]);

  const allValidatedCount = useMemo(
    () => filtered.filter(p => overrides[p.id]).length, [filtered, overrides]
  );

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllSuggested = () => {
    const next = new Set();
    filtered.forEach(p => { if (overrides[p.id]) next.add(p.id); });
    setSelected(next);
  };

  const clearSelection = () => setSelected(new Set());

  const itemsToApply = useMemo(() => Array.from(selected).map(pid => ({
    product_id: Number(pid),
    category_id: Number(overrides[pid]),
  })).filter(i => i.category_id), [selected, overrides]);

  const applyNow = async () => {
    setApplying(true);
    setApplyResults(null);
    try {
      const res = await fetch(`${API_BASE}/admin/products/apply-categories`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemsToApply),
      });
      const body = await res.json();
      setApplyResults(body);
      setShowConfirm(false);
      // Refresh after a short pause so the BO has time to propagate.
      setTimeout(fetchAll, 1500);
    } catch (err) {
      setApplyResults({ applied: 0, failed: itemsToApply.length, error: String(err) });
    } finally {
      setApplying(false);
    }
  };

  const downloadCsv = () => {
    if (!data?.products?.length) return;
    const rows = [['REF', 'NOM', 'CACHE', 'CAT_SUGGEREE', 'PREFIXE', 'CONFIANCE', 'ID_BO'],
      ...data.products.map(p => [
        p.ref || '', (p.name || '').replace(/[\r\n]+/g, ' '),
        p.hidden ? 'OUI' : 'NON',
        p.suggested_category_name || '',
        p.suggestion_prefix || '',
        p.suggestion_confidence ?? '',
        p.id ?? '',
      ])];
    const csv = rows.map(r => r.map(c => {
      const v = String(c);
      return /[;"\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
    }).join(';')).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `produits-sans-categorie-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  return (
    <Card className="rounded-none">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
            <div>
              <CardTitle className="text-base">Produits sans catégorie</CardTitle>
              <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
                Produits du Backoffice sans <code>ecommerce_category_ids</code>. La colonne
                <strong> Catégorie suggérée</strong> est calculée automatiquement à partir des préfixes SKU
                des produits déjà catégorisés. Validez (ou modifiez) chaque suggestion puis cliquez
                <strong> Appliquer la sélection</strong> — rien n&apos;est envoyé au Backoffice avant ce clic.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} className="rounded-none" data-testid="refresh-uncategorized-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Rafraîchir</span>
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!data?.products?.length} className="rounded-none" data-testid="export-uncategorized-csv-btn">
              <Download className="w-4 h-4" /><span className="ml-2 hidden sm:inline">CSV</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-4">{error}</div>}

        {data && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
              <div>
                <span className="font-semibold">{data.uncategorized_count}</span>
                <span className="text-muted-foreground"> sans catégorie</span>
                <span className="text-muted-foreground"> · {allValidatedCount} avec suggestion</span>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Input type="text" placeholder="Rechercher REF ou NOM…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none h-8 text-sm" data-testid="uncategorized-search-input" />
              </div>
              <button type="button" onClick={() => setHideHidden(v => !v)} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary" data-testid="toggle-hidden-products-btn">
                {hideHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {hideHidden ? 'Cachés masqués' : 'Cachés affichés'}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Button size="sm" variant="outline" onClick={selectAllSuggested} className="rounded-none text-xs h-7" data-testid="select-all-suggested-btn">
                Tout sélectionner avec suggestion ({filtered.filter(p => overrides[p.id]).length})
              </Button>
              <Button size="sm" variant="outline" onClick={clearSelection} className="rounded-none text-xs h-7" disabled={selected.size === 0}>
                Désélectionner ({selected.size})
              </Button>
              <div className="flex-1" />
              <Button
                size="sm"
                onClick={() => setShowConfirm(true)}
                disabled={itemsToApply.length === 0 || applying}
                className="rounded-none gap-2"
                data-testid="apply-categories-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Appliquer la sélection ({itemsToApply.length})
              </Button>
            </div>

            {applyResults && (
              <div className={`p-3 mb-4 border text-sm ${applyResults.failed > 0 ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-green-50 border-green-300 text-green-900'}`} data-testid="apply-results-banner">
                {applyResults.applied} produits mis à jour dans le Backoffice
                {applyResults.failed > 0 && <> · <strong>{applyResults.failed} échecs</strong> (voir détails ci-dessous)</>}
                {applyResults.results && applyResults.failed > 0 && (
                  <ul className="mt-2 text-xs space-y-1">
                    {applyResults.results.filter(r => !r.success).slice(0, 10).map((r, i) => (
                      <li key={i}>· Produit #{r.product_id} — HTTP {r.status} — {r.error}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        )}

        {data && (
          <div className="border overflow-x-auto" data-testid="uncategorized-products-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-3 py-2 font-semibold w-28">REF</th>
                  <th className="px-3 py-2 font-semibold">NOM</th>
                  <th className="px-3 py-2 font-semibold">CATÉGORIE SUGGÉRÉE / CHOISIE</th>
                  <th className="px-3 py-2 font-semibold w-16 text-center">CACHÉ</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted-foreground py-8">
                    {data.uncategorized_count === 0 ? 'Tous les produits sont catégorisés. 🎉' : 'Aucun produit ne correspond aux filtres.'}
                  </td></tr>
                )}
                {filtered.map((p) => {
                  const chosen = overrides[p.id] || '';
                  const isSuggested = !!p.suggested_category_id;
                  return (
                    <tr key={p.id} className={`border-t ${p.hidden ? 'opacity-60' : ''}`} data-testid="uncategorized-product-row">
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={() => toggleSelected(p.id)}
                          disabled={!chosen}
                          className="cursor-pointer disabled:opacity-30"
                          data-testid={`uncat-row-select-${p.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{p.ref || '—'}</td>
                      <td className="px-3 py-2">{p.name || <span className="text-muted-foreground italic">(sans nom)</span>}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <select
                            value={chosen}
                            onChange={(e) => setOverrides(prev => ({ ...prev, [p.id]: e.target.value ? Number(e.target.value) : undefined }))}
                            className="rounded-none border bg-white px-2 py-1 text-xs flex-1 min-w-[200px]"
                            data-testid={`uncat-category-select-${p.id}`}
                          >
                            <option value="">— Aucune (laisser sans catégorie) —</option>
                            {categories.map(c => (
                              <option key={c.id} value={c.id}>{c.name} — {c.path}</option>
                            ))}
                          </select>
                          {isSuggested && (
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                              suggéré ({p.suggestion_prefix}, conf. {Math.round((p.suggestion_confidence || 0) * 100)}%)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.hidden ? <span className="text-xs bg-gray-200 px-2 py-0.5">oui</span> : <span className="text-xs text-muted-foreground">non</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* CONFIRMATION MODAL — nothing is pushed to BO before clicking "Confirmer" */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="apply-confirm-modal">
          <div className="bg-white max-w-lg w-full rounded-none border shadow-xl">
            <div className="p-5 border-b flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-lg">Confirmer l&apos;application sur le Backoffice</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Vous êtes sur le point d&apos;assigner une catégorie à <strong>{itemsToApply.length} produit(s)</strong> directement
                  dans le Backoffice Ruby. Cette action est <strong>immédiate et visible côté ERP</strong>.
                </p>
              </div>
            </div>
            <div className="p-5 max-h-80 overflow-y-auto text-sm">
              <ul className="space-y-1 font-mono text-xs">
                {itemsToApply.slice(0, 30).map(it => {
                  const p = (data?.products || []).find(x => x.id === it.product_id);
                  const c = categories.find(x => x.id === it.category_id);
                  return <li key={it.product_id}>• <strong>{p?.ref || '?'}</strong> ({p?.name || '—'}) → {c?.name || '?'}</li>;
                })}
                {itemsToApply.length > 30 && <li className="italic text-muted-foreground">… et {itemsToApply.length - 30} autres</li>}
              </ul>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={applying} className="rounded-none" data-testid="apply-confirm-cancel">
                <XCircle className="w-4 h-4 mr-2" /> Annuler
              </Button>
              <Button onClick={applyNow} disabled={applying} className="rounded-none" data-testid="apply-confirm-ok">
                {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Application…</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirmer et envoyer au BO</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default UncategorizedProductsPanel;
